
import React from 'react';
import { format } from 'date-fns';
import { WeatherData } from '@/types/weather';

interface ImageDownloaderProps {
  weatherData: WeatherData;
  imageRef: React.RefObject<HTMLDivElement>;
}

export const ImageDownloader = ({ weatherData, imageRef }: ImageDownloaderProps) => {
  const handleDownload = async () => {
    if (!imageRef.current) return;

    try {
      const html2canvasModule = await import('html2canvas');
      const html2canvas = html2canvasModule.default;
      
      // Create a wrapper div with exact dimensions and added padding
      const wrapper = document.createElement('div');
      wrapper.style.width = '900px';
      wrapper.style.height = '900px';
      wrapper.style.position = 'absolute';
      wrapper.style.top = '-9999px';
      wrapper.style.left = '-9999px';
      wrapper.style.backgroundColor = 'transparent';
      document.body.appendChild(wrapper);
      
      // Clone the content with added padding
      const clone = imageRef.current.cloneNode(true) as HTMLElement;
      clone.style.width = '900px';
      clone.style.height = '900px';
      clone.style.borderRadius = '0'; // Remove rounded corners
      clone.style.overflow = 'hidden';
      clone.style.position = 'relative';
      
      // Add 20% internal padding to prevent content from being cut off (increased from 10%)
      const contentContainer = document.createElement('div');
      contentContainer.style.position = 'absolute';
      contentContainer.style.top = '20%';    // Increased padding (was 10%)
      contentContainer.style.left = '20%';   // Increased padding (was 10%)
      contentContainer.style.width = '60%';  // Reduced width (was 80%)
      contentContainer.style.height = '60%'; // Reduced height (was 80%)
      contentContainer.style.overflow = 'hidden';
      
      // Move all child nodes to the padded container
      while (clone.firstChild) {
        contentContainer.appendChild(clone.firstChild);
      }
      
      clone.appendChild(contentContainer);
      
      // Add logo at the top center
      const logo = document.createElement('div');
      logo.style.position = 'absolute';
      logo.style.top = '5%'; // Position closer to top but not too close
      logo.style.left = '0';
      logo.style.width = '100%';
      logo.style.display = 'flex';
      logo.style.justifyContent = 'center';
      logo.style.alignItems = 'center';
      logo.style.zIndex = '20';
      
      const logoImg = document.createElement('img');
      logoImg.src = '/lovable-uploads/763e44e8-ef8f-4c41-8496-a43ea9e27d72.png';
      logoImg.style.height = '40px';
      logoImg.style.width = 'auto';
      logoImg.style.filter = 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))';
      
      logo.appendChild(logoImg);
      
      // Add disclaimer at the bottom
      const disclaimer = document.createElement('div');
      disclaimer.style.position = 'absolute';
      disclaimer.style.bottom = '5%'; // Increased distance from bottom
      disclaimer.style.left = '0';
      disclaimer.style.width = '100%';
      disclaimer.style.textAlign = 'center';
      disclaimer.style.padding = '8px 10px'; // Larger padding
      disclaimer.style.fontSize = '14px'; // Larger font size
      disclaimer.style.color = 'rgba(255,255,255,0.95)'; // More visible text
      disclaimer.style.textShadow = '0 1px 2px rgba(0,0,0,0.9)'; // Stronger text shadow
      disclaimer.style.backgroundColor = 'rgba(0,0,0,0.5)'; // Slightly more opaque background
      disclaimer.textContent = '⚠️ For entertainment purposes only. Not responsible for usage. Visit: meteowish.com';
      
      // Remove any size restrictions from child elements
      const elementsToFix = contentContainer.querySelectorAll('*');
      elementsToFix.forEach(el => {
        const element = el as HTMLElement;
        if (element.style.maxWidth) element.style.maxWidth = 'none';
        if (element.style.maxHeight) element.style.maxHeight = 'none';
        // Scale down text elements for better fit with more padding
        if (element.tagName === 'H1' || element.tagName === 'H2' || element.tagName === 'H3' || 
            element.tagName === 'P' || element.tagName === 'DIV') {
          const currentFontSize = window.getComputedStyle(element).fontSize;
          if (currentFontSize) {
            const size = parseFloat(currentFontSize);
            if (size > 12) { // Only scale down larger text
              element.style.fontSize = `${size * 0.9}px`; // Scale down by 10%
            }
          }
        }
      });
      
      wrapper.appendChild(clone);
      clone.appendChild(logo);
      clone.appendChild(disclaimer);
      
      // Capture the content
      const canvas = await html2canvas(wrapper, {
        scale: 2, // Higher quality
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        width: 900,
        height: 900,
        x: 0,
        y: 0,
        scrollX: 0,
        scrollY: 0
      });
      
      // Clean up
      document.body.removeChild(wrapper);
      
      const image = canvas.toDataURL('image/png');
      
      const downloadLink = document.createElement('a');
      downloadLink.href = image;
      downloadLink.download = `meteo-${weatherData.location}-${format(weatherData.date, 'yyyy-MM-dd')}.png`;
      downloadLink.click();
    } catch (error) {
      console.error('Error during image download:', error);
    }
  };

  return { handleDownload };
};

export default ImageDownloader;
